const rotateVector = (X, Y, psi) => {
    return {
        x: X * Math.cos(psi) - Y * Math.sin(psi),
        y: X * Math.sin(psi) + Y * Math.cos(psi)
    };
};

const vectorToProjection = (X, Y, a) => {
    a = a % (2 * Math.PI);
    let m;
    let dirX, dirY;
    if (a < Math.PI / 2) {
        m = a;
        dirX = 1;
        dirY = 1;
    } else if (a < Math.PI) {
        m = Math.PI - a;
        dirX = -1;
        dirY = -1;
    } else if (a < Math.PI * 3 / 2) {
        m = a - Math.PI;
        dirX = -1;
        dirY = -1;
    } else {
        m = Math.PI * 2 - a;
        dirX = 1;
        dirY = 1;
    }

    console.log(X, Y);

    const x = dirX * X * Math.cos(m);
    const y = dirY * Y * Math.cos(m);

    return {x, y};
};

/**
 *
 * @param X: projection value in X-axis
 * @param Y: projection value in Y-axis
 * @param a: angle between vector and projection
 * @return {{x: number, y: number}}
 */
const projectionToVector = (X, Y, a) => {
    // a = a % (2 * Math.PI);

    // Give X, Y, a
    // M = sqrt(X^2 + Y^2)
    // m = arcTan(Y / X)
    // n = m - a
    // x = M * cos(n)
    // y = M * sin(n)

    let m, n;
    if (X === 0) {
        m = Math.PI / 2;
        if (Y >= 0) {
            n = m - a;
        } else {
            n = Math.PI + m - a;
        }
    } else {
        m = Math.atan(Y / X);
        if (X > 0) {
            n = m - a;
        } else {
            n = Math.PI + m - a;
        }
    }
    const M = Math.sqrt(X*X + Y*Y);
    const x = M * Math.cos(n);
    const y = M * Math.sin(n);
    return {x, y};
};

const getRotatedRectWrapper = (rectWidth, rectHeight, rectRotateRadians) => {
    const cross = Math.sqrt(rectWidth * rectWidth + rectHeight * rectHeight);
    const alpha = Math.atan(rectHeight / rectWidth);
    const gamma = Math.abs(rectRotateRadians) % Math.PI;

    let wrapperWidth1, wrapperHeight1, wrapperWidth2, wrapperHeight2;

    // cross 1
    const zeta1 = gamma + alpha;
    if (gamma < Math.PI / 2) {
        wrapperWidth1 = cross * Math.cos(zeta1);
        wrapperHeight1 = cross * Math.sin(zeta1);
    } else {
        wrapperWidth1 = cross * Math.sin(zeta1 - Math.PI / 2);
        wrapperHeight1 = cross * Math.cos(zeta1 - Math.PI / 2);
    }

    // cross 2
    const zeta2 = gamma - alpha;
    if (gamma < Math.PI / 2) {
        wrapperWidth2 = cross * Math.cos(zeta2);
        wrapperHeight2 = cross * Math.sin(zeta2);
    } else {
        wrapperWidth2 = cross * Math.sin(zeta2 - Math.PI / 2);
        wrapperHeight2 = cross * Math.cos(zeta2 - Math.PI / 2);
    }

    const wrapperWidth = Math.max(wrapperWidth1, wrapperWidth2);
    const wrapperHeight = Math.max(wrapperHeight1, wrapperHeight2);

    return {width: wrapperWidth, height: wrapperHeight};
};

const getRotatedRectFitInWrapper = (wrapperWidth, wrapperHeight, rectRotateRadians, rectAspectRatio) => {

    // rect separate wrapper into 2 couples of triangle: P = P', Q = Q'
    // sum of first squared edge of P and first squared edge of Q equals to wrapper width
    // sum of second squared edge of P and second squared edge of Q equals to wrapper height

    // Pa, Pb, Pc, Qa, Qb, Qc;
    // sqrt(Pa^2 + Pb^2) = Pc = rectWidth
    // sqrt(Qa^2 + Qb^2) = Qc = rectHeight
    // "(Pb, Pc) = gamma
    // "(Qb, Qc) = PI/2 - gamma

    // -----------------
    // when gamma = PI/4

    // We have a quaternary equation:

    // [1]. Pa + Qa = W = wrapperWidth
    // [2]. Pb + Qb = H = wrapperHeight
    // [3]. Pa / Pb = tan(gamma) = Tg
    // [4]. sqrt(Pa^2 + Pb^2) / sqrt(Qa^2 + Qb^2) = rectAspectRatio = A
    // cannot use this when gamma = PI/4
    // Qa / Qb = tan(PI/2 - gamma) = Tg'

    // [2+3]. Pa / Tg + Qb = H
    // [5]. [1+2+4]. Pa^2 + Pb^2 = AA.((W - Pa)^2 + (H - Pb)^2)
    
    // [5+3].
    // (Tg.Pb)^2 + Pb^2 = AA.(W^2 - 2.W.Tg.Pb + (Tg.Pb)^2 + H^2 - 2.H.Pb + Pb^2)
    // Tg^2.Pb^2 + Pb^2 = AA.W^2 - 2.AA.W.Tg.Pb + AA.Tg^2.Pb^2 + AA.H^2 - 2.AA.H.Pb + AA.Pb^2
    // (Tg^2 + 1 - AA.Tg^2 - AA).Pb^2 + (2.AA.W.Tg + 2.AA.H).Pb + (- AA.W^2 - AA.H^2) = 0

    // a = (1 - AA).(Tg^2 + 1)
    // b = 2.AA.(W.Tg + H)
    // c = -AA(W^2 + H^2)
    // delta = b^2 - 4.a.c

    // Pb = (-b + sqrt(delta)) / (2.a)
    // Pa = (-b - sqrt(delta)) / (2.a)

    // ------------------
    // when gamma != PI/4

    // We have a quaternary equation:
    // [1]. Pa + Qa = W = wrapperWidth
    // [2]. Pb + Qb = H = wrapperHeight
    // [3]. Pa / Pb = tan(gamma) = Tg
    // [4]. Qa / Qb = tan(PI/2 - gamma) = Tg'

    // [1+4]. Pa + Qb * Tg' = W
    // [2+3]. Pa / Tg + Qb = H

    // [1+2+3+4].
    // Pa + (H - Pa / Tg) * Tg' = W
    // Pa + H * Tg' - Pa * Tg' / Tg = W
    // Pa * (1 - Tg' / Tg) = W - H * Tg'

    // Pa = (W - H * Tg') / (1 - Tg' / Tg)
    // Qa = W - Pa
    // Qb = H - Pa / Tg
    // Pb = H - Qb

    const alpha = Math.abs(rectRotateRadians) % Math.PI;
    const gamma = alpha % (Math.PI / 2);

    // console.log('rotate', rectRotateRadians * 180 / Math.PI);
    // console.log('alpha', alpha * 180 / Math.PI);
    // console.log('gamma', gamma * 180 / Math.PI);

    let rectWidth, rectHeight;

    if (gamma === 0) {
        rectWidth = wrapperWidth;
        rectHeight = wrapperHeight;
    }
    else if (gamma === Math.PI / 2) {
        rectWidth = wrapperWidth;
        rectHeight = wrapperHeight;
    }
    else if (gamma === Math.PI / 4) {
        // TODO: find a simply approach

        const W = wrapperWidth;
        const H = wrapperHeight;
        const Tg = Math.tan(gamma);
        const A = rectAspectRatio;
        const AA = A*A;

        const $a = (1 - AA) * (Tg * Tg + 1);
        const $b = 2 * AA * (W * Tg + H);
        const $c = -AA * (W * W + H * H);

        const $delta = $b * $b - 4 * $a * $c;
        const Pb = (-$b + Math.sqrt($delta)) / (2 * $a);
        const Pa = Tg * Pb;

        const Pc = Math.sqrt(Pa * Pa + Pb * Pb);
        const Qc = Pc / A;

        rectWidth = Pc;
        rectHeight = Qc;
    }
    else {
        const W = wrapperWidth;
        const H = wrapperHeight;
        const Tg = Math.tan(gamma);
        const Tz = Math.tan(Math.PI / 2 - gamma); // Tg'

        const Pa = (W - H * Tz) / (1 - Tz / Tg);
        const Qa = W - Pa;
        const Qb = H - Pa / Tg;
        const Pb = H - Qb;

        const Pc = Math.sqrt(Pa * Pa + Pb * Pb);
        const Qc = Math.sqrt(Qa * Qa + Qb * Qb);

        if (alpha < Math.PI / 2) {
            rectWidth = Qc;
            rectHeight = Pc;
        } else {
            rectWidth = Pc;
            rectHeight = Qc;
        }
    }

    return {width: rectWidth, height: rectHeight};
};

/**
 *
 * @param {HTMLCanvasElement} canvas
 * @param {number} rotateRadians
 * @param {string} blankColor
 * @param {function} getIsBlank
 * @return {{left: (number|null), top: (number|null), right: (number|null), bottom: (number|null), wrapperWidth: number, wrapperHeight: number}}
 */
const getCanvasPixelsBoundingRect = (canvas, rotateRadians, blankColor, getIsBlank) => {
    const rotateWrapperRect = getRotatedRectWrapper(canvas.width, canvas.height, rotateRadians);
    const wrapperCanvas = document.createElement('canvas');
    wrapperCanvas.width = rotateWrapperRect.width;
    wrapperCanvas.height = rotateWrapperRect.height;
    const wrapperContext = wrapperCanvas.getContext('2d');

    wrapperContext.save();
    wrapperContext.fillStyle = blankColor;
    wrapperContext.fillRect(0, 0, wrapperCanvas.width, wrapperCanvas.height);
    wrapperContext.restore();

    wrapperContext.save();
    wrapperContext.translate(wrapperCanvas.width / 2, wrapperCanvas.height / 2);
    wrapperContext.rotate(rotateRadians);
    wrapperContext.scale(canvas.width / wrapperCanvas.width, canvas.height / wrapperCanvas.height);
    wrapperContext.translate(- wrapperCanvas.width / 2, - wrapperCanvas.height / 2);
    wrapperContext.drawImage(canvas,
        0, 0, canvas.width, canvas.height,
        0, 0, wrapperCanvas.width, wrapperCanvas.height
    );
    wrapperContext.restore();

    const {data, width, height} = wrapperContext.getImageData(0, 0, wrapperCanvas.width, wrapperCanvas.height);

    // these variables use 0-based
    let left = null, top = null, right = null, bottom = null;

    for (let i = 0; i < data.length; i += 4) {
        const row = Math.floor(i / 4 / width);
        const column = i / 4 % width;
        const red = data[i];
        const green = data[i+1];
        const blue = data[i+2];
        const alpha = data[i + 3];

        const isBlank = getIsBlank(red, green, blue, alpha);

        if (!isBlank) {
            if (left === null || left > column) left = column;
            if (right === null || right < column) right = column;
            if (top === null) top = row;
            bottom = row;
        }
    }

    return {left, top, right, bottom, wrapperWidth: wrapperCanvas.width, wrapperHeight: wrapperCanvas.height};
};

module.exports = {
    rotateVector,
    projectionToVector,
    getRotatedRectWrapper,
    getRotatedRectFitInWrapper,
    getCanvasPixelsBoundingRect,
};